import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Gift, Lock, Sparkles, ShoppingCart, Heart, Swords, Zap, Eye, Star, X, Coins, Rocket } from 'lucide-react';

const Store = ({ user, onBack, onUpdateUser }) => {
  const [dailyGift, setDailyGift] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [beyblades, setBeyblades] = useState([]);
  const [purchasing, setPurchasing] = useState(false);
  const [purchasingBP, setPurchasingBP] = useState(false);

  // YouTube unlock token states
  const [youtubeTokenClaimed, setYoutubeTokenClaimed] = useState(false);
  const [unlockTokens, setUnlockTokens] = useState(0);
  const [claimingYoutubeToken, setClaimingYoutubeToken] = useState(false);

  // State for confirmation modal
  const [confirmPurchase, setConfirmPurchase] = useState(null);
  const [spinningId, setSpinningId] = useState(null);
  const [rarityFilter, setRarityFilter] = useState('all');
  const [dualSpinPhase, setDualSpinPhase] = useState('right'); // For Gravity Perseus alternating spin
  const dualSpinIntervalRef = useRef(null);
  
  // State for stats popup (long-press)
  const [statsPopup, setStatsPopup] = useState(null);
  const longPressTimerRef = useRef(null);
  const longPressDuration = 500; // 500ms for long press

  // Beyblade stats data
  // Get beyblade rarity
  const getBeybladeRarity = (id) => {
    if ([1, 5, 6, 57, 78].includes(id)) return 'mythic'; // Glory Valkyrie (78) is Mythic
    if ([14, 15, 16, 17, 18, 58, 62, 63, 64, 68, 69, 75, 76, 77, 79, 80].includes(id)) return 'legendary'; // Glory Valkyrie (79) and Drake Brave (80) are Legendary
    if ([14, 15, 16, 17, 18, 58, 62, 63, 64, 68, 69, 75, 76, 77].includes(id)) return 'legendary'; // Added Dran Buster C1, C2, C3 (75, 76, 77)
    if ([7, 19, 20, 21, 29, 30, 31, 32, 41, 42, 43, 44, 49, 56, 59, 61, 67, 70, 71, 72, 81].includes(id)) return 'rare'; // Added Luster Dragoon (81)
    if ([60, 65, 66, 3, 4, 25, 26, 33, 34, 35, 36, 50, 51].includes(id)) return 'uncommon'; // Added Phoenix Wing B (66)
    return 'common';
  };

  // Get rarity colors for the popup
  const getRarityColors = (rarity) => {
    switch (rarity) {
      case 'mythic':
        return {
          border: 'border-purple-500',
          bg: 'from-purple-900/90 via-purple-800/80 to-purple-900/90',
          accent: 'text-purple-400',
          statBg: 'bg-purple-800/50'
        };
      case 'legendary':
        return {
          border: 'border-yellow-500',
          bg: 'from-yellow-900/90 via-orange-800/80 to-yellow-900/90',
          accent: 'text-yellow-400',
          statBg: 'bg-yellow-800/30'
        };
      case 'rare':
        return {
          border: 'border-blue-500',
          bg: 'from-blue-900/90 via-blue-800/80 to-blue-900/90',
          accent: 'text-blue-400',
          statBg: 'bg-blue-800/50'
        };
      case 'uncommon':
        return {
          border: 'border-green-500',
          bg: 'from-green-900/90 via-emerald-800/80 to-green-900/90',
          accent: 'text-green-400',
          statBg: 'bg-green-800/50'
        };
      default: // common
        return {
          border: 'border-cyan-500',
          bg: 'from-slate-900/90 via-slate-800/80 to-slate-900/90',
          accent: 'text-cyan-400',
          statBg: 'bg-slate-800/50'
        };
    }
  };

  const getBeybladeStats = (id) => {
    const statsMap = {
      // Cobalt Drake (ID 1) - 50 sec stamina, speed 100, 10 HP, has Iron Wall
      1: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Iron Wall - Invulnerable to damage and knockback for 4 seconds',
        spinDirection: 'Right'
      },
      // Dran Sword (ID 2) - 50 sec stamina, speed 100, has Final Drive
      2: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Phoenix Wing (ID 3) - 50 sec stamina, speed 100
      3: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Aero Pegasus (ID 5) - has Final Drive
      5: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Valkyrie Volt (ID 6) - 40 sec stamina, 11 HP
      6: {
        stamina: 40,
        health: 11,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Wizard Arrow (ID 8) - 1 min 45 sec stamina, has Restore Health
      8: {
        stamina: 105,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'Restore Health - Fully recovers health points once per battle',
        spinDirection: 'Right'
      },
      // Hells Scythe (ID 9) - Speed 75, 1 min 40 sec stamina, 10 HP
      9: {
        stamina: 100,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 75,
        hiddenAbility: 'None',
        specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback',
        spinDirection: 'Right'
      },
      // Knight Shield (ID 10) - 15 HP, has Iron Wall
      10: {
        stamina: 90,
        health: 15,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'Iron Wall - Immune to knockback and damage for 4 seconds',
        spinDirection: 'Right'
      },
      // Phoenix Wing CC (ID 15) - 50 sec stamina, speed 100
      15: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Dran Buster (ID 22) - High damage, low stamina, 2x knockback
      22: {
        stamina: 30,
        health: 10,
        basicDamage: 4,
        boostDamage: 8,
        speed: 70,
        hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Hells Hammer (ID 23) - has Burning Strike (same as Hells Scythe)
      23: {
        stamina: 100,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 75,
        hiddenAbility: 'None',
        specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback',
        spinDirection: 'Right'
      },
      // Wizard Rod (ID 24) - High stamina, Restore Health ability
      24: {
        stamina: 120,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'Restore Health - Fully recovers health points once per battle',
        spinDirection: 'Right'
      },
      // Wizard Rod G (ID 57) - Mythic, same as Wizard Rod, Restore Health ability
      57: {
        stamina: 120,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'Restore Health - Fully recovers health points once per battle',
        spinDirection: 'Right'
      },
      // Phoenix Feather (ID 58) - Legendary, same as Phoenix Wing
      58: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Dran Brave B (ID 59) - Rare, same as Dran Sword, has Final Drive
      59: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Dran Brave (ID 37) - same as Dran Sword, has Final Drive
      37: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Dran Dagger (ID 60) - Uncommon, same as Dran Sword, has Final Drive
      60: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Cobalt Drake C (ID 61) - Rare, same as Cobalt Drake, has Iron Wall
      61: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Iron Wall - Invulnerable to damage and knockback for 4 seconds',
        spinDirection: 'Right'
      },
      // Cobalt Drake R (ID 62) - Legendary, same as Cobalt Drake, has Iron Wall
      62: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Iron Wall - Invulnerable to damage and knockback for 4 seconds',
        spinDirection: 'Right'
      },
      // Samurai Saber C (ID 63) - Legendary, same as Samurai Saber (default stats)
      63: {
        stamina: 90,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Dran Buster C (ID 64) - Legendary, same as Dran Buster, has Power Knockback
      64: {
        stamina: 30,
        health: 10,
        basicDamage: 4,
        boostDamage: 8,
        speed: 70,
        hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Wizard Rod C (ID 65) - Uncommon, same as Wizard Rod, has Restore Health
      65: {
        stamina: 120,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'Restore Health - Fully recovers health points once per battle',
        spinDirection: 'Right'
      },
      // Phoenix Wing B (ID 66) - Uncommon, same as Phoenix Wing
      66: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Phoenix Flare (ID 67) - Rare, same as Hells Scythe, has Burning Strike
      67: {
        stamina: 100,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 75,
        hiddenAbility: 'None',
        specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback',
        spinDirection: 'Right'
      },
      // Fox Brush (ID 70) - Rare, same as Hells Scythe
      70: {
        stamina: 100,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 75,
        hiddenAbility: 'None',
        specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback',
        spinDirection: 'Right'
      },
      // Fox Brush G (ID 71) - Rare, same as Hells Scythe
      71: {
        stamina: 100,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 75,
        hiddenAbility: 'None',
        specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback',
        spinDirection: 'Right'
      },
      // Fox Brush B (ID 72) - Rare, same as Hells Scythe
      72: {
        stamina: 100,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 75,
        hiddenAbility: 'None',
        specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback',
        spinDirection: 'Right'
      },
      // Phoenix Rudder (ID 50) - same as Phoenix Flare/Hells Scythe, has Burning Strike
      50: {
        stamina: 100,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 75,
        hiddenAbility: 'None',
        specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback',
        spinDirection: 'Right'
      },
      // Phoenix Wing M (ID 68) - Legendary, same as Phoenix Wing
      68: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Pegasus Blast R (ID 69) - Legendary, same as Pegasus Blast, has Final Drive
      69: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Dran Buster C1 (ID 75) - Legendary, same as Dran Buster
      75: {
        stamina: 30,
        health: 10,
        basicDamage: 4,
        boostDamage: 8,
        speed: 70,
        hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Dran Buster C2 (ID 76) - Legendary, same as Dran Buster
      76: {
        stamina: 30,
        health: 10,
        basicDamage: 4,
        boostDamage: 8,
        speed: 70,
        hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Dran Buster C3 (ID 77) - Legendary, same as Dran Buster
      77: {
        stamina: 30,
        health: 10,
        basicDamage: 4,
        boostDamage: 8,
        speed: 70,
        hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Glory Valkyrie (ID 78) - Mythic, same as Aero Pegasus
      78: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Glory Valkyrie 2 (ID 79) - Legendary, same as Aero Pegasus
      79: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Drake Brave (ID 80) - Legendary, same as Aero Pegasus
      80: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Luster Dragoon (ID 81) - Rare, same as LL Drago, Left Spin
      81: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds',
        spinDirection: 'Left'
      },
      // Wizard Arc (ID 38) - Restore Health ability
      38: {
        stamina: 120,
        health: 8,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'Restore Health - Fully recovers health points once per battle',
        spinDirection: 'Right'
      },
      // Shinobi Shadow (ID 36) - Clone ability
      36: {
        stamina: 90,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'Shadow Clones - Spawns 5 clones and teleports to a random clone position',
        spinDirection: 'Right'
      },
      // Knight Mail (ID 31) - Iron Wall ability
      31: {
        stamina: 60,
        health: 12,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'Iron Wall - Immune to knockback and damage for 4 seconds',
        spinDirection: 'Right'
      },
      // Knight Mail B (ID 16) - Iron Wall ability
      16: {
        stamina: 60,
        health: 12,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'Iron Wall - Immune to knockback and damage for 4 seconds',
        spinDirection: 'Right'
      },
      // Storm Pegasus (ID 45) - has Final Drive
      45: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Victory Valkyrie (ID 48) - same as Phoenix Wing
      48: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Clock Mirage (ID 49) - Rewind Time ability, very high stamina
      49: {
        stamina: 180,
        health: 7,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'Rewind Time - Teleports all beyblades to positions from 2 seconds ago',
        spinDirection: 'Right'
      },
      // Galaxy Pegasus (ID 53) - has Final Drive
      53: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Gravity Perseus (ID 55) - Dual spin
      55: {
        stamina: 90,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'Dual Spin - Can switch between left and right spin during battle',
        spinDirection: 'Dual (Left/Right)'
      },
      // Vari Ares (ID 56) - Dual spin (same as Gravity Perseus)
      56: {
        stamina: 90,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'None',
        specialMove: 'Dual Spin - Can switch between left and right spin during battle',
        spinDirection: 'Dual (Left/Right)'
      },
      // Meteo L Drago (ID 54) - Spin Steal, Left Spin, has Final Drive
      54: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'Spin Steal - Regains health & stamina when damaging right-spinning opponents',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds',
        spinDirection: 'Left'
      },
      // Meteor Dragoon (ID 42) - Spin Steal, Left Spin, has Final Drive
      42: {
        stamina: 90,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'Spin Steal - Regains health & stamina when damaging right-spinning opponents',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds',
        spinDirection: 'Left'
      },
      // Left-spin beyblades with Final Drive: Cobalt Dragoon (4), Cobalt Dragoon B (18), LL Drago (47)
      4: {
        stamina: 90,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds',
        spinDirection: 'Left'
      },
      18: {
        stamina: 90,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds',
        spinDirection: 'Left'
      },
      47: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds',
        spinDirection: 'Left'
      },
      // Hells Reaper (ID 40) - has Burning Strike (same as Hells Scythe)
      40: {
        stamina: 100,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 75,
        hiddenAbility: 'None',
        specialMove: 'Burning Strike - Fire rotates around beyblade for 2 seconds, deals 2 damage on contact, immune to damage/knockback',
        spinDirection: 'Right'
      },
      // Xeno Xcaliber (ID 52) - 40 sec stamina, high damage
      52: {
        stamina: 40,
        health: 10,
        basicDamage: 3,
        boostDamage: 6,
        speed: 60,
        hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Impact Drake (ID 30) - 50 sec stamina, 2x knockback, speed 90
      30: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 90,
        hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed',
        specialMove: 'None',
        spinDirection: 'Right'
      },
      // Scorpio Spear (ID 32) - has Iron Wall + Power Knockback
      32: {
        stamina: 90,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 50,
        hiddenAbility: 'Power Knockback - Knocks back opponents at twice the speed',
        specialMove: 'Iron Wall - Immune to knockback and damage for 4 seconds',
        spinDirection: 'Right'
      },
      // Pegasus Blast (ID 21) - has Final Drive ability
      21: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Pegasus Brush (ID 44) - has Final Drive
      44: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Ultimate Valkyrie (ID 73) - Common, same as Dran Sword
      73: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 3, boost damage to 4 for 4 seconds',
        spinDirection: 'Right'
      },
      // Guilty Longinus (ID 74) - Common, same as LL Drago (Left Spin)
      74: {
        stamina: 50,
        health: 10,
        basicDamage: 1,
        boostDamage: 2,
        speed: 100,
        hiddenAbility: 'None',
        specialMove: 'Final Drive - Halves stamina, increases speed to 200%, basic damage to 2, boost damage to 4 for 4 seconds',
        spinDirection: 'Left'
      }
    };
    
    // Return specific stats or default stats
    return statsMap[id] || {
      stamina: 90,
      health: 10,
      basicDamage: 1,
      boostDamage: 2,
      speed: 50,
      hiddenAbility: 'None',
      specialMove: 'None',
      spinDirection: 'Right'
    };
  };

  // Long press handlers
  const handleLongPressStart = useCallback((beyblade) => {
    longPressTimerRef.current = setTimeout(() => {
      const stats = getBeybladeStats(beyblade.id);
      const rarity = getBeybladeRarity(beyblade.id);
      const colors = getRarityColors(rarity);
      setStatsPopup({ ...beyblade, stats, rarity, colors });
    }, longPressDuration);
  }, []);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);
  
  // Image loading state
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [imageRetries, setImageRetries] = useState({});
  const imageRetryTimeouts = useRef({});
  const [imagesLoaded, setImagesLoaded] = useState({});
  const preloadedImages = useRef({});

  // Beyblade image mapping (from InteractiveMap)
  const getBeybladeImage = (id) => {
    const beybladeImages = {
      1: 'https://customer-assets.emergentagent.com/job_blader-progress/artifacts/zw2i6ecd_IMG_0908.png',
      2: 'https://customer-assets.emergentagent.com/job_beypark/artifacts/b6xmsvw0_IMG_0911.png',
      3: 'https://customer-assets.emergentagent.com/job_battle-tops-3/artifacts/9gd0mo3x_IMG_0918.png',
      4: 'https://customer-assets.emergentagent.com/job_battle-tops-3/artifacts/eqx6fcua_IMG_0920.png',
      5: 'https://customer-assets.emergentagent.com/job_57de14b1-dd79-40f8-9905-e428eae842bc/artifacts/ae815y75_IMG_0922.png',
      6: 'https://customer-assets.emergentagent.com/job_57de14b1-dd79-40f8-9905-e428eae842bc/artifacts/2rl351jz_IMG_0926.png',
      7: 'https://customer-assets.emergentagent.com/job_57de14b1-dd79-40f8-9905-e428eae842bc/artifacts/018lhj90_IMG_0932.png',
      8: 'https://customer-assets.emergentagent.com/job_beyduel-game/artifacts/lh4lkr8d_IMG_0951.png',
      9: 'https://customer-assets.emergentagent.com/job_beyduel-game/artifacts/xv1i2whp_IMG_0953.png',
      10: 'https://customer-assets.emergentagent.com/job_beyduel-game/artifacts/3b1i2oge_IMG_0956.png',
      11: 'https://customer-assets.emergentagent.com/job_beyduel-game/artifacts/tai6ttk9_IMG_0959.png',
      12: 'https://customer-assets.emergentagent.com/job_beyduel-game/artifacts/4ch9bvsm_IMG_0961.png',
      13: 'https://customer-assets.emergentagent.com/job_beyduel-game/artifacts/vhc53hi5_IMG_0963.png',
      14: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/aeeivbh5_IMG_0975.png',
      15: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/i2zfqebx_IMG_0983.png',
      16: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/setl3t4g_IMG_0982.png',
      17: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/b056b1rw_IMG_0993.png',
      18: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/1bscobtv_IMG_0995.png',
      19: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/dddzndhc_IMG_0998.png',
      20: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/zo0593i3_IMG_0999.png',
      21: 'https://customer-assets.emergentagent.com/job_battle-arena-577/artifacts/nzo7v3hh_IMG_1002.png',
      22: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/j3vumbrl_IMG_1006.png',
      23: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/jupug5bm_IMG_1008.png',
      24: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/vjqujewp_IMG_1010.png',
      25: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/93whrda1_IMG_1004.png',
      26: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/fdvt1mcs_IMG_1014.png',
      27: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/l5wqu0eq_IMG_1019.png',
      28: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/3ys9urgf_IMG_1016.png',
      29: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/cq1rrejw_IMG_1056.png',
      30: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/uyzyvs0f_IMG_1061.png',
      31: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/gbc3cbr4_IMG_1062.png',
      32: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/p8r05bmr_IMG_1066.png',
      33: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/pdnh7yfj_IMG_1058.png',
      34: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/1i79j7t9_IMG_1070.png',
      35: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/gf3r8pwx_IMG_1064.png',
      36: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/hqvirsc4_IMG_1071.png',
      37: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/z29ujjed_IMG_1081.png',
      38: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/llk6d0je_IMG_1083.png',
      39: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/75v5o0yr_IMG_1082.png',
      40: 'https://customer-assets.emergentagent.com/job_beyblade-battles/artifacts/up29j0hg_IMG_1085.png',
      41: 'https://customer-assets.emergentagent.com/job_beyblades-battle/artifacts/2ip00mdr_IMG_1077.png',
      42: 'https://customer-assets.emergentagent.com/job_beyblades-battle/artifacts/0fgghl73_IMG_1012.png',
      43: 'https://customer-assets.emergentagent.com/job_beyblades-battle/artifacts/kxtfgb7q_IMG_1115.png',
      44: 'https://customer-assets.emergentagent.com/job_beyblades-battle/artifacts/e1w2jid2_IMG_1117.png',
      45: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/zbtxlzwg_IMG_1200.png',
      46: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/rtz6hrx9_IMG_1201.png',
      47: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/y327vnu9_IMG_1202.png',
      48: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/0up5sihr_IMG_1203.png',
      49: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/7vaa4m9i_IMG_1087.png',
      50: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/8jzkk3oo_IMG_1075.png',
      51: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/3yuc0fhg_IMG_1072.png',
      52: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/541bmhls_IMG_1204.png',
      53: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/tfp4tn0i_IMG_1238.png',
      54: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/kp5i34f1_IMG_1239.png',
      55: 'https://customer-assets.emergentagent.com/job_beyblade-arena-4/artifacts/vncu9cse_IMG_1244.png',
      56: 'https://customer-assets.emergentagent.com/job_battle-arena-626/artifacts/84u59dh1_IMG_1254.png',
      57: 'https://customer-assets.emergentagent.com/job_8cf175b2-6a18-4f7b-949a-e3aab798aba9/artifacts/yfj0jsjj_IMG_1643.png',
      58: 'https://customer-assets.emergentagent.com/job_8cf175b2-6a18-4f7b-949a-e3aab798aba9/artifacts/rrmnl4t8_IMG_1645.png',
      59: 'https://customer-assets.emergentagent.com/job_8cf175b2-6a18-4f7b-949a-e3aab798aba9/artifacts/iinrjbke_IMG_1652.png',
      60: 'https://customer-assets.emergentagent.com/job_8cf175b2-6a18-4f7b-949a-e3aab798aba9/artifacts/wvp4odkd_IMG_1656.png',
      61: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/6eav4612_IMG_1650.png',
      62: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/oqx5w08s_IMG_1651.png',
      63: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/ht26xzfl_IMG_1670.png',
      64: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/xwtosigy_IMG_1674.png',
      65: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/v1p00bju_IMG_1680.png',
      66: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/qllo84cv_IMG_1682.png',
      67: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/sb0yvasr_IMG_1668.png',
      68: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/u4qospky_IMG_1683.png',
      69: 'https://customer-assets.emergentagent.com/job_bey-battlezone/artifacts/ehmmk8ey_IMG_1685.png',
      70: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/arcjnokl_IMG_1705.png',
      71: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/hnsc5oqv_IMG_1706.png',
      72: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/1wwswe1a_IMG_1707.png',
      73: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/aa5rsuge_IMG_1732.png',
      74: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/yi2wexk5_IMG_1731.png',
      75: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/l40px0ao_IMG_1714.png',
      76: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/o7nexuz8_IMG_1712.png',
      77: 'https://customer-assets.emergentagent.com/job_spin-battle-3/artifacts/44x7o4l9_IMG_1713.png',
      78: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_80d2796e-4c98-4810-b14d-894ef7171682/artifacts/6ad1cdeb1c66c362_IMG_6835.png',
      79: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_80d2796e-4c98-4810-b14d-894ef7171682/artifacts/c9711bd34d55590b_IMG_4322.png',
      80: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_80d2796e-4c98-4810-b14d-894ef7171682/artifacts/b927dc7095c4e505_IMG_6841.png',
      81: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_80d2796e-4c98-4810-b14d-894ef7171682/artifacts/6a9e4d7e8d9d10c6_IMG_6843.png',
    };
    return beybladeImages[id] || '';
  };

  const handleBeybladeClick = (beybladeId) => {
    // Start spinning animation
    setSpinningId(beybladeId);
    
    // Special handling for dual-spin beyblades (Gravity Perseus id 55, Vari Ares id 56)
    if (beybladeId === 55 || beybladeId === 56) {
      setDualSpinPhase('right'); // Start with right spin
      // Clear any existing interval
      if (dualSpinIntervalRef.current) {
        clearInterval(dualSpinIntervalRef.current);
      }
      // Switch to left spin after 1.5 seconds
      dualSpinIntervalRef.current = setTimeout(() => {
        setDualSpinPhase('left');
      }, 1500);
      // Stop after 3 seconds total (1.5s right + 1.5s left)
      setTimeout(() => {
        if (dualSpinIntervalRef.current) {
          clearTimeout(dualSpinIntervalRef.current);
          dualSpinIntervalRef.current = null;
        }
        setSpinningId(null);
      }, 3000);
    } else {
      setTimeout(() => setSpinningId(null), 2000);
    }
  };

  // Handle image load error with retry logic
  const handleImageError = (beybladeId) => {
    const currentRetries = imageRetries[beybladeId] || 0;
    const maxRetries = 3;
    
    if (currentRetries < maxRetries) {
      // Exponential backoff: 1s, 2s, 4s
      const retryDelay = Math.pow(2, currentRetries) * 1000;
      
      console.log(`Image ${beybladeId} failed to load. Retry ${currentRetries + 1}/${maxRetries} in ${retryDelay}ms`);
      
      // Clear any existing timeout for this image
      if (imageRetryTimeouts.current[beybladeId]) {
        clearTimeout(imageRetryTimeouts.current[beybladeId]);
      }
      
      // Schedule retry
      imageRetryTimeouts.current[beybladeId] = setTimeout(() => {
        setImageRetries(prev => ({
          ...prev,
          [beybladeId]: currentRetries + 1
        }));
        // Force re-render by updating error state
        setImageLoadErrors(prev => ({
          ...prev,
          [beybladeId]: false
        }));
      }, retryDelay);
    } else {
      // Max retries reached, mark as failed
      console.error(`Image ${beybladeId} failed to load after ${maxRetries} retries`);
      setImageLoadErrors(prev => ({
        ...prev,
        [beybladeId]: true
      }));
    }
  };
  
  // Get image URL with cache-busting for retries
  const getImageUrlWithRetry = (beybladeId) => {
    const baseUrl = getBeybladeImage(beybladeId);
    const retryCount = imageRetries[beybladeId] || 0;
    if (retryCount > 0) {
      return `${baseUrl}?retry=${retryCount}`;
    }
    return baseUrl;
  };

  // Preload all beyblade images on mount to prevent reloading when filtering
  useEffect(() => {
    const beybladeIds = Array.from({ length: 81 }, (_, i) => i + 1);
    beybladeIds.forEach(id => {
      const url = getBeybladeImage(id);
      if (url && !preloadedImages.current[id]) {
        const img = new Image();
        img.onload = () => {
          preloadedImages.current[id] = img;
          setImagesLoaded(prev => ({ ...prev, [id]: true }));
          // Clear any previous error state if image loads successfully
          setImageLoadErrors(prev => ({ ...prev, [id]: false }));
        };
        img.onerror = () => {
          // Will be handled by the component's error handler
          console.log(`Preload failed for beyblade ${id}`);
        };
        img.src = url;
        preloadedImages.current[id] = img;
      }
    });
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(imageRetryTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  useEffect(() => {
    fetchDailyGift();
    fetchBeyblades();
    fetchYoutubeToken();
  }, []);

  const fetchYoutubeToken = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/store/youtube-token`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setYoutubeTokenClaimed(response.data.claimed);
      setUnlockTokens(response.data.tokens || 0);
    } catch (error) {
      console.error('Error fetching YouTube token status:', error);
    }
  };

  const handleClaimYoutubeToken = async () => {
    setClaimingYoutubeToken(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/store/youtube-token/claim`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setYoutubeTokenClaimed(true);
      setUnlockTokens(response.data.tokens || 1);
    } catch (error) {
      console.error('Error claiming YouTube token:', error);
      toast.error(error.response?.data?.detail || 'Failed to claim token');
    } finally {
      setClaimingYoutubeToken(false);
    }
  };

  const fetchDailyGift = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/store/daily-gift`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyGift(response.data);
    } catch (error) {
      console.error('Error fetching daily gift:', error);
    }
  };

  const fetchBeyblades = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/store/beyblades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBeyblades(response.data.beyblades);
    } catch (error) {
      console.error('Error fetching beyblades:', error);
    }
  };

  const handleClaimDailyGift = async () => {
    if (!dailyGift?.available) return;
    
    setClaiming(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/store/daily-gift/claim`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      await fetchDailyGift();
      onUpdateUser(); // Refresh user data to show new BP
    } catch (error) {
      console.error('Error claiming daily gift:', error);
      toast.error(error.response?.data?.detail || 'Failed to claim daily gift');
    } finally {
      setClaiming(false);
    }
  };

  const handlePurchaseBP = async () => {
    setPurchasingBP(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/purchase/create-checkout`, {
        package_id: "100bp",
        origin_url: originUrl
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to initiate purchase');
    } finally {
      setPurchasingBP(false);
    }
  };

  const handleBeybladePurchase = async (useToken = false) => {
    if (!confirmPurchase) return;
    
    setPurchasing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/store/beyblades/purchase`, 
        { beyblade_id: confirmPurchase.id, use_youtube_token: useToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      
      // If token was used, update the local token count
      if (response.data.token_used) {
        setUnlockTokens(response.data.tokens_remaining || 0);
      }
      
      await fetchBeyblades();
      onUpdateUser(); // Refresh user data
      setConfirmPurchase(null); // Close modal
    } catch (error) {
      console.error('Error purchasing beyblade:', error);
      toast.error(error.response?.data?.detail || 'Failed to purchase Beyblade');
    } finally {
      setPurchasing(false);
    }
  };

  const handlePurchase = async (amount) => {
    setPurchasing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/store/purchase`, 
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Successfully purchased ${amount} BP!`);
      
      if (response.data.daily_gift_unlocked) {
        toast.success('🎁 Daily gift unlocked!');
      }
      
      await fetchDailyGift();
      onUpdateUser(); // Refresh user data
    } catch (error) {
      console.error('Error purchasing BP:', error);
      toast.error('Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const packages = [
    { amount: 100, price: '$0.99', popular: false },
    { amount: 500, price: '$3.99', popular: true },
    { amount: 1000, price: '$6.99', popular: false },
    { amount: 5000, price: '$29.99', popular: false }
  ];

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white select-none"
      style={{
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        touchAction: 'manipulation'
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 p-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-cyan-400 digital-font flex items-center gap-2">
            <Gift className="w-7 h-7 text-red-500" />
            STORE
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Daily Gift Section */}
        <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-2 border-purple-500/50 rounded-lg p-6 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-pink-500 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 p-3 rounded-lg">
                  <Gift className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white digital-font">DAILY GIFT</h2>
                  <p className="text-sm text-purple-300">Free 5 BP every day!</p>
                </div>
              </div>
              {dailyGift?.locked && (
                <Lock className="w-6 h-6 text-slate-400" />
              )}
            </div>

            <p className="text-slate-300 mb-4">{dailyGift?.message}</p>

            <div className="flex gap-3">
              <Button
                onClick={handleClaimDailyGift}
                disabled={!dailyGift?.available || claiming}
                className={`flex-1 ${
                  dailyGift?.available
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
                    : 'bg-slate-700 cursor-not-allowed'
                }`}
              >
                {claiming ? (
                  'Claiming...'
                ) : dailyGift?.locked ? (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Unlock with first purchase
                  </>
                ) : dailyGift?.available ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Claim Daily Gift
                  </>
                ) : (
                  'Already Claimed Today'
                )}
              </Button>

              <Button
                onClick={handlePurchaseBP}
                disabled={purchasingBP}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {purchasingBP ? 'Processing...' : '100BP   $1'}
              </Button>
            </div>

            {/* YouTube Subscription Token Section */}
            <div className="mt-4 pt-4 border-t border-purple-500/30">
              {youtubeTokenClaimed ? (
                <div className="text-center">
                  <p className="text-sm text-green-400 font-medium mb-1">
                    ✅ Thank you for Subscribing to our YouTube Channel!
                  </p>
                  <p className="text-xs text-slate-300 mb-2">
                    Watch daily videos for epic battles and pro strategies!
                  </p>
                  <a
                    href="https://youtube.com/@beybladexdash?si=fGWxEE1BVeyt-Uzs"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white text-xs px-4 py-2 rounded-md font-medium transition-all"
                  >
                    BeybladeXDash
                  </a>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-yellow-300 mb-2">
                    🎁 <span className="font-bold">Claim a FREE Beyblade!</span> Unlock a token by subscribing to the YouTube Channel{' '}
                    <a 
                      href="https://youtube.com/@beybladexdash?si=fGWxEE1BVeyt-Uzs"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        // Open YouTube channel and claim token after a short delay
                        setTimeout(() => {
                          if (!youtubeTokenClaimed && !claimingYoutubeToken) {
                            handleClaimYoutubeToken();
                          }
                        }, 2000);
                      }}
                      className="text-red-400 font-bold underline hover:text-red-300 transition-colors"
                    >
                      BeybladeXDash
                    </a>!
                  </p>
                  <Button
                    onClick={() => {
                      window.open('https://youtube.com/@beybladexdash?si=fGWxEE1BVeyt-Uzs', '_blank');
                      setTimeout(() => {
                        if (!youtubeTokenClaimed && !claimingYoutubeToken) {
                          handleClaimYoutubeToken();
                        }
                      }, 2000);
                    }}
                    disabled={claimingYoutubeToken}
                    className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white text-xs px-4 py-2"
                  >
                    {claimingYoutubeToken ? 'Claiming...' : '🎬 Subscribe & Get Free Token'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Beyblade Store Section */}
        <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border-2 border-cyan-500/50 rounded-lg p-6">
          <div className="mb-4 text-center">
            <h2 className="text-2xl font-bold text-cyan-400 digital-font mb-2">BEYBLADE SHOP</h2>
            <p className="text-sm text-slate-300 mb-4">Purchase Beyblades with Battle Points</p>
            
            {/* Rarity Filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-2">
              <button
                onClick={() => setRarityFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  rarityFilter === 'all'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                    : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setRarityFilter('common')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  rarityFilter === 'common'
                    ? 'bg-cyan-600 text-white border-2 border-cyan-400'
                    : 'bg-slate-700/50 text-cyan-300 hover:bg-slate-600/50'
                }`}
              >
                Common
              </button>
              <button
                onClick={() => setRarityFilter('uncommon')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  rarityFilter === 'uncommon'
                    ? 'bg-green-700 text-white border-2 border-green-400'
                    : 'bg-slate-700/50 text-green-300 hover:bg-slate-600/50'
                }`}
              >
                Uncommon
              </button>
              <button
                onClick={() => setRarityFilter('rare')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  rarityFilter === 'rare'
                    ? 'bg-blue-800 text-white border-2 border-blue-400'
                    : 'bg-slate-700/50 text-blue-300 hover:bg-slate-600/50'
                }`}
              >
                Rare
              </button>
              <button
                onClick={() => setRarityFilter('legendary')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  rarityFilter === 'legendary'
                    ? 'bg-gradient-to-r from-yellow-600 to-orange-600 text-white border-2 border-yellow-400'
                    : 'bg-slate-700/50 text-yellow-300 hover:bg-slate-600/50'
                }`}
              >
                Legendary
              </button>
              <button
                onClick={() => setRarityFilter('mythic')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  rarityFilter === 'mythic'
                    ? 'bg-purple-700 text-white border-2 border-purple-400'
                    : 'bg-slate-700/50 text-purple-300 hover:bg-slate-600/50'
                }`}
              >
                Mythic
              </button>
            </div>
            
            {/* Hint text for long press */}
            <p className="text-xs text-yellow-400 italic mt-3">Long Press on Beyblades to check stats!</p>
          </div>

          {/* Hidden preloader - keeps all images in DOM to prevent reload */}
          <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
            {beyblades.map((b) => (
              <img key={`preload-${b.id}`} src={getBeybladeImage(b.id)} alt="" />
            ))}
          </div>
          
          <div className="grid grid-cols-4 gap-2">
            {beyblades.map((beyblade) => {
              // Determine rarity for filtering
              const isPremium = [1, 5, 6, 57, 78].includes(beyblade.id); // Mythic - includes Glory Valkyrie (78)
              const isGoldTier = [14, 15, 16, 17, 18, 58, 62, 63, 64, 68, 69, 75, 76, 77, 79, 80].includes(beyblade.id); // Legendary - includes Glory Valkyrie (79) and Drake Brave (80)
              const isDarkBlueTier = [7, 19, 20, 21, 29, 30, 31, 32, 41, 42, 43, 44, 49, 56, 59, 61, 67, 70, 71, 72, 81].includes(beyblade.id); // Rare - Added Luster Dragoon (81)
              const isGreenTier = [60, 65, 66, 3, 4, 25, 26, 33, 34, 35, 36, 50, 51].includes(beyblade.id); // Uncommon - Added Phoenix Wing B (66)
              const isCommon = !isPremium && !isGoldTier && !isDarkBlueTier && !isGreenTier; // Common
              
              // Check if beyblade should be visible based on filter
              let isVisible = rarityFilter === 'all';
              if (rarityFilter === 'mythic') isVisible = isPremium;
              else if (rarityFilter === 'legendary') isVisible = isGoldTier;
              else if (rarityFilter === 'rare') isVisible = isDarkBlueTier;
              else if (rarityFilter === 'uncommon') isVisible = isGreenTier;
              else if (rarityFilter === 'common') isVisible = isCommon;
              else if (rarityFilter === 'all') isVisible = true;
              
              const beybladeImageUrl = getImageUrlWithRetry(beyblade.id);
              const isSpinning = spinningId === beyblade.id;
              const shouldSpinReverse = [4, 18, 42, 47, 54, 74, 81].includes(beyblade.id); // Cobalt Dragoon, Cobalt Dragoon B, Meteor Dragoon, LL Drago, Meteo L Drago, Guilty Longinus, and Luster Dragoon spin reverse (left spin)
              const isDualSpin = beyblade.id === 55 || beyblade.id === 56; // Gravity Perseus and Vari Ares
              
              // Determine spin direction for animation
              // CSS 'spin' keyframe goes 0->360deg (clockwise), 'reverse' goes counter-clockwise
              let spinAnimation = '';
              if (isSpinning) {
                if (isDualSpin) {
                  // Gravity Perseus alternates based on dualSpinPhase
                  spinAnimation = dualSpinPhase === 'left' 
                    ? 'spin 0.3s linear infinite reverse'  // Left = counter-clockwise
                    : 'spin 0.3s linear infinite';         // Right = clockwise
                } else {
                  // Left-spin beyblades spin counter-clockwise, right-spin spin clockwise
                  spinAnimation = shouldSpinReverse 
                    ? 'spin 0.3s linear infinite reverse'  // Left-spin beyblades = counter-clockwise
                    : 'spin 0.3s linear infinite';         // Right-spin beyblades = clockwise
                }
              }
              
              // Always render the same structure - hidden items collapse but images stay cached
              return (
                <div 
                  key={beyblade.id} 
                  className={`flex-col gap-1 ${isVisible ? 'flex' : 'hidden'}`}
                >
                  <div
                    onClick={() => handleBeybladeClick(beyblade.id)}
                    onMouseDown={() => handleLongPressStart(beyblade)}
                    onMouseUp={handleLongPressEnd}
                    onMouseLeave={handleLongPressEnd}
                    onTouchStart={() => handleLongPressStart(beyblade)}
                    onTouchEnd={handleLongPressEnd}
                    onTouchCancel={handleLongPressEnd}
                    className={`relative aspect-square rounded border-2 flex flex-col items-center justify-center transition-all ${
                      isGoldTier
                        ? 'border-yellow-500/70 bg-gradient-to-br from-yellow-600/40 to-orange-600/40 hover:from-yellow-600/50 hover:to-orange-600/50 cursor-pointer'
                        : isDarkBlueTier
                        ? 'border-blue-500/70 bg-gradient-to-br from-blue-800/60 to-blue-900/60 hover:from-blue-800/70 hover:to-blue-900/70 cursor-pointer'
                        : isGreenTier
                        ? 'border-green-500/70 bg-gradient-to-br from-green-700/50 to-emerald-800/50 hover:from-green-700/60 hover:to-emerald-800/60 cursor-pointer'
                        : isPremium
                        ? 'border-purple-500/70 bg-purple-900/40 hover:bg-purple-800/50 cursor-pointer'
                        : 'border-cyan-500/50 bg-slate-700/50 hover:bg-cyan-900/30 cursor-pointer'
                    }`}
                  >
                    {imageLoadErrors[beyblade.id] ? (
                      // Fallback when image fails to load after retries
                      <div className="w-16 h-16 mt-2 flex items-center justify-center bg-slate-800/50 rounded-lg border border-slate-600">
                        <span className="text-slate-500 text-xs">No Image</span>
                      </div>
                    ) : (
                      <img 
                        src={beybladeImageUrl} 
                        alt={beyblade.name} 
                        draggable="false"
                        className={`w-16 h-16 object-contain mt-2`}
                        style={isSpinning ? { animation: spinAnimation } : {}}
                        onError={() => handleImageError(beyblade.id)}
                      />
                    )}
                    <p className="text-cyan-300 text-[8px] mt-1 text-center px-1">{beyblade.name}</p>
                    {beyblade.owned && (
                      <div className="absolute top-1 right-1 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                        ✓
                      </div>
                    )}
                    {/* Unlock Token Indicator - shows coin with count when tokens available and beyblade not owned */}
                    {unlockTokens > 0 && !beyblade.owned && (
                      <div 
                        className="absolute top-1 left-1 flex items-center gap-0.5 rounded-full"
                        style={{
                          animation: 'goldShimmer 1.5s ease-in-out infinite',
                          boxShadow: '0 0 4px 2px rgba(255, 215, 0, 0.6)',
                        }}
                      >
                        <span className="text-[7px]">🪙</span>
                        <span className="text-[6px] text-yellow-300 font-bold">{unlockTokens}</span>
                      </div>
                    )}
                    <style>{`
                      @keyframes goldShimmer {
                        0%, 100% { box-shadow: 0 0 4px 2px rgba(255, 215, 0, 0.4); }
                        50% { box-shadow: 0 0 8px 4px rgba(255, 215, 0, 0.8); }
                      }
                    `}</style>
                  </div>
                  
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-yellow-400 font-bold text-sm">{beyblade.price}</span>
                    <span className="text-slate-400 text-xs">BP</span>
                  </div>

                  <button
                    onClick={() => setConfirmPurchase({ id: beyblade.id, name: beyblade.name, price: beyblade.price })}
                    disabled={beyblade.owned || purchasing}
                    className={`px-2 py-1 rounded text-xs font-bold transition-all ${
                      beyblade.owned
                        ? 'bg-green-700/50 text-green-300 cursor-not-allowed'
                        : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white'
                    }`}
                  >
                    {beyblade.owned ? 'Owned' : 'Buy'}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Confirmation Modal */}
          {confirmPurchase && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setConfirmPurchase(null)}>
              <div className="bg-slate-800 border-2 border-cyan-500 rounded-lg p-6 max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-xl font-bold text-cyan-400 mb-4">Confirm Purchase</h3>
                <p className="text-white mb-2">Are you sure you want to purchase:</p>
                <p className="text-cyan-300 font-bold text-lg mb-4">{confirmPurchase.name}?</p>
                <div className="flex items-center justify-center gap-2 mb-6">
                  <span className="text-3xl font-bold text-yellow-400">{confirmPurchase.price}</span>
                  <span className="text-slate-400">BP</span>
                </div>
                
                {/* Show free token option if available */}
                {unlockTokens > 0 && (
                  <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
                    <p className="text-yellow-300 text-sm text-center mb-2">
                      🪙 You have {unlockTokens} unlock token{unlockTokens > 1 ? 's' : ''}!
                    </p>
                    <Button
                      onClick={() => handleBeybladePurchase(true)}
                      disabled={purchasing}
                      className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold"
                    >
                      {purchasing ? 'Claiming...' : '🎁 Get FREE with Token'}
                    </Button>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => setConfirmPurchase(null)}
                    className="flex-1 bg-slate-600 hover:bg-slate-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleBeybladePurchase(false)}
                    disabled={purchasing}
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                  >
                    {purchasing ? 'Purchasing...' : `Buy ${confirmPurchase.price} BP`}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Stats Popup Modal (Long Press) */}
          {statsPopup && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setStatsPopup(null)}>
              <div 
                className={`bg-gradient-to-br ${statsPopup.colors.bg} border-2 ${statsPopup.colors.border} rounded-xl p-5 w-80 mx-4 relative`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button 
                  onClick={() => setStatsPopup(null)}
                  className="absolute top-2 right-2 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                {/* Beyblade Image and Name */}
                <div className="flex flex-col items-center mb-4">
                  <img 
                    src={getBeybladeImage(statsPopup.id)} 
                    alt={statsPopup.name}
                    className="w-24 h-24 object-contain mb-2"
                  />
                  <h3 className={`text-xl font-bold ${statsPopup.colors.accent} digital-font text-center`}>{statsPopup.name}</h3>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-yellow-400 font-bold">{statsPopup.price}</span>
                    <span className="text-slate-400 text-sm">BP</span>
                  </div>
                </div>

                {/* Stats Grid - Fixed height for consistency */}
                <div className="space-y-2">
                  {/* Stamina Bar */}
                  <div className="flex items-center gap-3 bg-slate-800/60 rounded-lg p-2 h-14">
                    <div className="w-5 h-5 flex items-center justify-center text-white font-bold flex-shrink-0">⏱️</div>
                    <div className="flex-1">
                      <div className="text-xs text-slate-400 mb-1">Stamina</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full w-full bg-gradient-to-r from-white to-gray-300"
                          />
                        </div>
                        <span className="text-white font-bold text-sm min-w-[45px] text-right">{Math.floor(statsPopup.stats.stamina / 60)}:{String(statsPopup.stats.stamina % 60).padStart(2, '0')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Health Bar */}
                  <div className="flex items-center gap-3 bg-slate-800/60 rounded-lg p-2 h-14">
                    <Heart className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs text-slate-400 mb-1">Health</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full w-full bg-gradient-to-r from-red-500 to-pink-500"
                          />
                        </div>
                        <span className="text-white font-bold text-sm min-w-[45px] text-right">{statsPopup.stats.health}/{statsPopup.stats.health}</span>
                      </div>
                    </div>
                  </div>

                  {/* Basic Damage */}
                  <div className="flex items-center gap-3 bg-slate-800/60 rounded-lg p-2 h-14">
                    <Swords className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs text-slate-400">Basic Damage</div>
                      <span className="text-white font-bold">{statsPopup.stats.basicDamage}</span>
                    </div>
                  </div>

                  {/* Boost Damage */}
                  <div className="flex items-center gap-3 bg-slate-800/60 rounded-lg p-2 h-14">
                    <Zap className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs text-slate-400">Boost Damage</div>
                      <span className="text-white font-bold">{statsPopup.stats.boostDamage}</span>
                    </div>
                  </div>

                  {/* Speed */}
                  <div className="flex items-center gap-3 bg-slate-800/60 rounded-lg p-2 h-14">
                    <div className="w-5 h-5 flex items-center justify-center text-green-400 font-bold flex-shrink-0">💨</div>
                    <div className="flex-1">
                      <div className="text-xs text-slate-400">Speed</div>
                      <span className="text-white font-bold">{statsPopup.stats.speed}%</span>
                    </div>
                  </div>

                  {/* Spin Direction */}
                  <div className="flex items-center gap-3 bg-slate-800/60 rounded-lg p-2 h-14">
                    <div className="w-5 h-5 flex items-center justify-center text-cyan-400 font-bold flex-shrink-0">↻</div>
                    <div className="flex-1">
                      <div className="text-xs text-slate-400">Spin Direction</div>
                      <span className={`font-bold ${
                        statsPopup.stats.spinDirection === 'Left' ? 'text-purple-400' :
                        statsPopup.stats.spinDirection.includes('Dual') ? 'bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent' :
                        'text-orange-400'
                      }`}>{statsPopup.stats.spinDirection}</span>
                    </div>
                  </div>

                  {/* Hidden Ability */}
                  <div className="flex items-start gap-3 bg-slate-800/60 rounded-lg p-2 min-h-[56px]">
                    <Eye className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs text-slate-400">Hidden Ability</div>
                      <span className={`text-sm leading-tight ${statsPopup.stats.hiddenAbility === 'None' ? 'text-slate-500' : 'text-purple-300'}`}>
                        {statsPopup.stats.hiddenAbility}
                      </span>
                    </div>
                  </div>

                  {/* Special Move */}
                  <div className="flex items-start gap-3 bg-slate-800/60 rounded-lg p-2 min-h-[56px]">
                    {[5, 21, 44, 45, 53, 2, 4, 18, 42, 47, 54, 37, 59, 60, 69].includes(statsPopup.id) ? (
                      <span className="text-xl mt-0.5 flex-shrink-0">🚀</span>
                    ) : [24, 38, 8, 57, 65].includes(statsPopup.id) ? (
                      <span className="text-xl mt-0.5 flex-shrink-0">💚</span>
                    ) : [31, 16, 10, 1, 61, 62].includes(statsPopup.id) ? (
                      <span className="text-xl mt-0.5 flex-shrink-0">🛡️</span>
                    ) : [9, 23, 40, 67, 50].includes(statsPopup.id) ? (
                      <span className="text-xl mt-0.5 flex-shrink-0">🔥</span>
                    ) : (
                      <Star className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <div className="text-xs text-slate-400">Special Move</div>
                      <span className={`text-sm leading-tight ${statsPopup.stats.specialMove === 'None' ? 'text-slate-500' : [5, 21, 44, 45, 53, 2, 4, 18, 42, 47, 54, 37, 59, 60, 69].includes(statsPopup.id) ? 'text-blue-300' : [24, 38, 8, 57, 65].includes(statsPopup.id) ? 'text-green-300' : [31, 16, 10, 1, 61, 62].includes(statsPopup.id) ? 'text-slate-300' : [9, 23, 40, 67, 50].includes(statsPopup.id) ? 'text-orange-300' : 'text-yellow-300'}`}>
                        {statsPopup.stats.specialMove}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Hint text */}
                <p className="text-xs text-slate-500 text-center mt-3">Tap outside or X to close</p>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Store;
